import type { AiChatMessage, AiChatMessageChunk, TransientChunk } from '../common/ai-chat/aiChatTypes';
import { BuildDefinitionStatus, ChatCompletionRequestMessageRole } from '@server/ai';
import type { AgentState } from './agentsChatsTypes';

type ChatDetails = AgentState['chatsDetails'][string];

const attachPendingRunId = (message: AiChatMessage, chatDetails: ChatDetails): AiChatMessage => {
  if (chatDetails.pendingRunId) {
    const messageWithMetadata = {
      ...message,
      runId: chatDetails.pendingRunId,
    };
    chatDetails.pendingRunId = undefined;
    return messageWithMetadata;
  }
  return message;
};

/**
 * Handles artifact metadata chunk - initializes artifact streaming state
 * and creates or marks the message as artifact type
 */
export const handleArtifactMetadata = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  chatDetails.artifactStreamingState = {
    artifactId: chunk.artifactMetadata!.artifactId,
    versionId: chunk.artifactMetadata!.versionId,
    title: chunk.artifactMetadata!.title,
    accumulatedContent: '',
    isComplete: false,
    lastProcessedLength: 0,
    hasCreatedFollowupMessage: false,
    toolResults: chunk.artifactMetadata!.toolResults,
  };

  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'artifact',
      artifactData: {
        artifactId: chunk.artifactMetadata!.artifactId,
        versionId: chunk.artifactMetadata!.versionId,
        title: chunk.artifactMetadata!.title,
      },
    };

    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    lastMessage.contentType = 'artifact';
    lastMessage.artifactData = {
      artifactId: chunk.artifactMetadata!.artifactId,
      versionId: chunk.artifactMetadata!.versionId,
      title: chunk.artifactMetadata!.title,
    };
  }
};

/**
 * Handles artifact content chunk - accumulates content and checks for completion
 */
export const handleArtifactContent = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
) => {
  if (!chatDetails.artifactStreamingState) return;

  chatDetails.artifactStreamingState.accumulatedContent += chunk.artifactContent!;

  const accumulated = chatDetails.artifactStreamingState.accumulatedContent;
  if (/\n```\s*$/.test(accumulated)) {
    chatDetails.artifactStreamingState.isComplete = true;
  }
};

/**
 * Handles build definition metadata chunk - sets streaming state, initializes the cache entry,
 * and creates an assistant message with contentType 'build_definition'.
 */
export const handleBuildDefinitionMetadata = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  const { buildDefinitionId } = chunk.buildDefinitionMetadata!;

  chatDetails.buildDefinitionStreamingState = { buildDefinitionId };

  if (!chatDetails.buildDefinitions) {
    chatDetails.buildDefinitions = {};
  }
  chatDetails.buildDefinitions[buildDefinitionId] = {
    text: '',
    status: BuildDefinitionStatus.Draft,
  };

  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'build_definition',
      buildDefinitionData: { buildDefinitionId },
    };
    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    lastMessage.contentType = 'build_definition';
    lastMessage.buildDefinitionData = { buildDefinitionId };
  }
};

/**
 * Handles build definition content chunk - appends text directly to the cache entry.
 */
export const handleBuildDefinitionContent = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
) => {
  if (!chatDetails.buildDefinitionStreamingState || !chatDetails.buildDefinitions) return;

  const { buildDefinitionId } = chatDetails.buildDefinitionStreamingState;
  const entry = chatDetails.buildDefinitions[buildDefinitionId];
  if (entry) {
    entry.text += chunk.buildDefinitionContent!;
  }
};

/**
 * Handles build definition status chunk - updates the status in the cache entry.
 * If the entry doesn't exist (e.g. page reload mid-build stream), bootstraps it
 * so reconnects correctly reflect the current status without dropping the event.
 */
export const handleBuildDefinitionStatus = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
) => {
  const { buildDefinitionId, status } = chunk.buildDefinitionStatus!;
  if (!chatDetails.buildDefinitions) {
    chatDetails.buildDefinitions = {};
  }
  if (!chatDetails.buildDefinitions[buildDefinitionId]) {
    chatDetails.buildDefinitions[buildDefinitionId] = { text: '', status: status as BuildDefinitionStatus };
  } else {
    chatDetails.buildDefinitions[buildDefinitionId].status = status as BuildDefinitionStatus;
  }
};

/**
 * Handles the first transient chunk (e.g., tool_explanation).
 *
 * If the last message is already an assistant message with transient chunks,
 * we update it instead of creating a new one. This handles the case where
 * multiple progress updates come via WebSocket with isFirstContentChunk: true
 * (needed to clear loading state) but we don't want duplicate messages.
 */
export const handleFirstTransientChunk = (
  transientChunk: TransientChunk,
  messages: AiChatMessage[],
  chatDetails: ChatDetails,
) => {
  const lastMessage = messages[messages.length - 1];

  if (
    lastMessage?.role === ChatCompletionRequestMessageRole.ASSISTANT &&
    lastMessage.transientChunks
  ) {
    handleSubsequentTransientChunk(transientChunk, messages);
    return;
  }

  const baseMessage: AiChatMessage = {
    role: ChatCompletionRequestMessageRole.ASSISTANT,
    content: '',
    createdAt: new Date().toISOString(),
    transientChunks: [transientChunk],
  };

  const newMessage = attachPendingRunId(baseMessage, chatDetails);
  messages.push(newMessage);
};

/**
 * Handles regular message content with special handling for post-artifact content
 */
export const handleMessageContent = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
  isFirstContentChunk: boolean,
) => {
  const lastMessage = messages[messages.length - 1];

  if (isFirstContentChunk) {
    const baseMessage: AiChatMessage = {
      ...chunk,
      content: chunk.content!,
      transientChunks: chunk.transientChunk ? [chunk.transientChunk] : undefined,
    };

    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    const shouldCreateFollowupMessage =
      chatDetails.artifactStreamingState?.isComplete &&
      !chatDetails.artifactStreamingState.hasCreatedFollowupMessage;

    if (shouldCreateFollowupMessage) {
      const baseMessage: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.ASSISTANT,
        content: chunk.content!,
        createdAt: new Date().toISOString(),
        isError: chunk.isError,
      };

      const newMessage = attachPendingRunId(baseMessage, chatDetails);
      messages.push(newMessage);
      chatDetails.artifactStreamingState!.hasCreatedFollowupMessage = true;
    } else {
      lastMessage.content += chunk.content!;
      lastMessage.isError = chunk.isError;
    }
  }
};


const RESEARCH_PROGRESS_PREFIX = 'Research progress:';

export const handleSubsequentTransientChunk = (
  transientChunk: TransientChunk,
  messages: AiChatMessage[],
) => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage.transientChunks) {
    lastMessage.transientChunks = [];
  }

  const lastChunk = lastMessage.transientChunks.at(-1);

  /**
   * This should be removed eventually together with the 
   * old research.
   */
  if (
    transientChunk.event === 'tool_explanation' &&
    lastChunk?.event === 'tool_explanation' &&
    transientChunk.data.startsWith(RESEARCH_PROGRESS_PREFIX) &&
    lastChunk.data.startsWith(RESEARCH_PROGRESS_PREFIX)
  ) {
    lastChunk.data = transientChunk.data;
    return;
  }

  if (transientChunk.event === 'thinking' && lastChunk?.event === 'thinking') {
    lastChunk.data += transientChunk.data;
    return;
  }

  if (transientChunk.event === 'tool_call' && lastChunk?.event === 'tool_call') {
    const [lastToolCall, lastAmount] = lastChunk.data.split(' x');
    if (lastToolCall === transientChunk.data) {
      lastChunk.data = `${lastToolCall} x${Number(lastAmount ?? 1) + 1}`;
      return;
    }
  }

  lastMessage.transientChunks.push(transientChunk);
};

/**
 * Handles questions-metadata chunk - pushes a new loading-state assistant message.
 * Marks the current last assistant message as having pending questions (loading state).
 * No new message is created — questions are attached to the same message as the streamed text,
 * mirroring how the persistent structure stores them.
 */
export const handleQuestionsMetadataChunk = (
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.role === ChatCompletionRequestMessageRole.ASSISTANT) {
    lastMessage.contentType = 'questions';
  } else {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'questions',
    };
    messages.push(attachPendingRunId(baseMessage, chatDetails));
  }
};

/**
 * Handles questions chunk - stores questions data on the last assistant message
 */
export const handleQuestionsChunk = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'questions',
      questionsData: chunk.questionsData,
    };
    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    lastMessage.contentType = 'questions';
    lastMessage.questionsData = chunk.questionsData;
  }
};
